import { KEY_VERSION } from './keys';

const appVersion = typeof VERSION !== 'undefined' && VERSION ? VERSION : 'latest'; // eslint-disable-line no-undef

/**
 * Adapter to use same methods whether
 * we chose to use SessionStorage, LocalStorage or Cookies.
 */
class Storage {
  /**
   * Constructor
   *
   * @param storage either CookieAdapter/LocalStorageAdapter/SessionStorageAdapter
   */
  constructor(storage) {
    this.storage = storage;
  }

  /**
   * Get the element 'key' in the store
   *
   * @param key
   * @return {*}
   */
  get(key) {
    return this.storage.get(key);
  }

  /**
   * Get the element 'key' of the current version in the store
   *
   * @param key
   * @return {*}
   */
  getWithVersion(key) {
    return this.storage.get(`${key}_${appVersion}`);
  }

  /**
   * Set the element 'key' in the store to 'value'
   *
   * @param key
   * @param value
   */
  set(key, value) {
    try {
      this.storage.set(key, value);
    } catch (e) {
      // Nothing to do, storage is full, also
      // on Safari, IOS 10.1 LocalStorage is disabled.
      //
      // If we use storage more intensively check
      // http://crocodillon.com/blog/always-catch-localstorage-security-and-quota-exceeded-errors
      // @TODO Find a way to send log to the backend
    }
  }

  /**
   * Set the element 'key' of the current version in the store to 'value'
   *
   * @param key
   * @param value
   */
  setWithVersion(key, value) {
    this.storage.set(`${key}_${appVersion}`, value);
  }

  /**
   * Set the element 'key' in the store to 'value'
   *
   * @param key
   * @param value
   * @param ttl expires in sec
   */
  setJSON(key, value, ttl) {
    this.storage.setJSON(key, value, ttl);
  }

  /**
   * Set the element 'key' of the current version in the store to 'value'
   *
   * @param key
   * @param value
   * @param ttl expires in sec
   */
  setJSONWithVersion(key, value, ttl) {
    this.storage.setJSON(`${key}_${appVersion}`, value, ttl);
  }

  /**
   * Get the element key in the store , it is supposed to be a JSON value.
   *
   * @param key
   * @return {*}
   */
  getJSON(key) {
    return this.storage.getJSON(key);
  }

  /**
   * Get the element key of the current version in the store , it is supposed to be a JSON value.
   *
   * @param key
   * @return {*}
   */
  getJSONWithVersion(key) {
    return this.storage.getJSON(`${key}_${appVersion}`);
  }

  /**
   * Remove element 'key' from storage
   *
   * @param key
   * @return {*}
   */
  remove(key) {
    return this.storage.remove(key);
  }

  /**
   * Remove element 'key' of the current version from storage
   *
   * @param key
   * @return {*}
   */
  removeWithVersion(key) {
    return this.storage.remove(`${key}_${appVersion}`);
  }

  /**
   * Clear all the storage
   *
   * @return {*}
   */
  clear() {
    return this.storage.clear();
  }

  /**
   * Return the number of elements in the storage
   *
   * @return {*}
   */
  length() {
    return this.storage.length;
  }

  /**
   * Get the version
   */
  getVersion() {
    return this.storage.get(KEY_VERSION);
  }

  /**
   * Set the version
   *
   * @param value
   */
  setVersion(value) {
    try {
      this.storage.set(KEY_VERSION, value);
    } catch (e) {
      // Nothing to do, storage is full, also
      // on Safari, IOS 10.1 LocalStorage is disabled.
      //
      // If we use storage more intensively check
      // http://crocodillon.com/blog/always-catch-localstorage-security-and-quota-exceeded-errors
      // @TODO Find a way to send log to the backend
    }
  }

  /**
   * Delete all keys that are suffixed by the version
   */
  removeByVersion(version) {
    if (version) {
      this.storage.removeByVersion(version);
    }
  }
}

export default Storage;
